import ApplicationModel from "../../../shared/models/applications/api/ApplicationModel";
import { defaultVoiceOption, getSparkVoiceOption, SparkVoiceOption, voiceOptions } from "../../../shared/constants/sparkConstants/voiceOptions";
import { updateVfyVoiceName } from "../../../shared/utils/vfyVoiceMarkup";
import { ApplicationModuleFieldLookup } from "../api/applicationModuleFields";
import {
    booleanAnswerToString,
    buildClosedGreeting,
    dayVariableMap,
    formatHoursForDisplay,
    formatSpecialDatesForDisplay,
    formatPhoneNumber,
    getFirstModuleFieldValue,
    getModuleFieldValue,
    getPhoneDigitsForSave,
    getSpecialDatesAnswerFromLookup,
    getWeeklyHoursAnswerFromLookup,
    getWeeklyHoursUpdates,
    InsuranceCoverageAnswerValue,
    inverseBooleanAnswerToString,
    OnboardingAnswerValue,
    parseBooleanString,
    parseInsuranceCoverageFromPrompt,
    parseSpecialDatesForSave,
    sanitizeMultilineText,
    serializeInsuranceCoverageToPrompt,
    WeeklyHoursAnswerValue,
    extractUserCustomText,
    formatLocationFaqParagraph
} from "../utils/onboardingFieldUtils";

export type OnboardingFlowType = "food-ordering" | "healthcare";
export type OnboardingQuestionInputType = "text" | "textarea" | "tel" | "hours" | "choice" | "select" | "email-list" | "item-list" | "currency" | "number" | "info" | "special-dates" | "procedure-types" | "insurance";
export type OnboardingQuestionOwnership = "organization" | "location" | "both" | "spark";

export interface OnboardingSaveInstruction {
    associatedVariable: string;
    value: string;
}

export interface OnboardingFlowBuildContext {
    application: ApplicationModel;
    moduleFieldLookup: ApplicationModuleFieldLookup;
    storedAnswers?: Record<string, unknown>;
}

export interface OnboardingQuestionConfig {
    id: string;
    title: string;
    helperText?: string;
    supportText?: string;
    instructionLeadIn?: string;
    fieldInstructions?: Array<{ label: string; text: string }>;
    emphasizeSupportText?: boolean;
    tooltipText?: string;
    continueLabel?: string;
    skipLabel?: string;
    placeholder?: string;
    itemPlaceholders?: string[];
    optional?: boolean;
    type: OnboardingQuestionInputType;
    section: string;
    associatedVariable?: string;
    associatedVariables?: string[];
    /**
     * Variables that determine where this question belongs. This is intentionally
     * separate from its primary associatedVariable because choice/mode questions
     * can save through inline child questions or custom save instructions.
     */
    ownershipVariables?: string[];
    /**
     * Fixed questions bypass Spark ownership detection. Questions without this
     * setting default to Spark-resolved ownership when they have real variables.
     */
    ownership?: OnboardingQuestionOwnership;
    /**
     * Response-only questions are saved in onboarding progress for the response
     * modal; their answers do not create or update module-form field values.
     */
    responseOnly?: boolean;
    /** Existing module field to move by title while keeping answers response-only. */
    locationPromotionFieldTitle?: string;
    /**
     * Lets the organization POC hand a response-only organization question to
     * each location POC. Only an explicit locationPromotionFieldTitle also
     * promotes an existing Spark field.
     */
    allowLocationOverride?: boolean;
    saveEnabled?: boolean;
    choices?: Array<{ label: string; value: string }>;
    allowMultipleChoices?: boolean;
    multiChoiceDelimiter?: string;
    choicePresentation?: "buttons" | "checkboxes";
    useModuleFieldOptions?: boolean;
    defaultValue?: string;
    getInitialValue?: (context: OnboardingFlowBuildContext) => OnboardingAnswerValue;
    normalizeInput?: (value: string) => string;
    isVisible?: (answers: Record<string, OnboardingAnswerValue>) => boolean;
    isValid?: (value: OnboardingAnswerValue) => boolean;
    getSaveInstructions?: (value: OnboardingAnswerValue, context: OnboardingFlowBuildContext) => OnboardingSaveInstruction[];
}

export interface OnboardingStepConfig {
    id: string;
    title: string;
    questions: OnboardingQuestionConfig[];
}

export interface OnboardingFlowConfig {
    type: OnboardingFlowType;
    title: string;
    steps: OnboardingStepConfig[];
}

const yesNoChoices = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" }
];

const paymentChoices = [
    { label: "Prepaid (texted payment link)", value: "prepaid" },
    { label: "Cash", value: "cash" },
    { label: "Card with driver", value: "card_with_driver" }
];

const callHandlingChoices = [
    { label: "Call Overflow", value: "callOverflow" },
    { label: "Off Hours", value: "offHours" }
];

const multiChoiceDelimiter = ",";

const parseMultiChoiceValue = (value: string, choiceOrder: string[]): string[] => {
    const selected = new Set((value || "")
        .split(multiChoiceDelimiter)
        .map(entry => entry.trim())
        .filter(Boolean));

    return choiceOrder.filter(choice => selected.has(choice));
};

const serializeMultiChoiceValue = (values: string[], choiceOrder: string[]): string => {
    const selected = new Set(values);
    return choiceOrder.filter(choice => selected.has(choice)).join(multiChoiceDelimiter);
};

const usTimeZoneChoices = [
    { label: "US/Eastern", value: "America/New_York" },
    { label: "US/Central", value: "America/Chicago" },
    { label: "US/Mountain", value: "America/Denver" },
    { label: "US/Pacific", value: "America/Los_Angeles" },
    { label: "US/Arizona", value: "America/Phoenix" },
    { label: "US/Alaska", value: "America/Anchorage" },
    { label: "US/Hawaii", value: "Pacific/Honolulu" }
];

const timeZoneAliases: Record<string, string> = {
    "america/new_york": "America/New_York",
    "america/detroit": "America/New_York",
    "america/indiana/indianapolis": "America/New_York",
    "us/eastern": "America/New_York",
    "america/chicago": "America/Chicago",
    "us/central": "America/Chicago",
    "america/denver": "America/Denver",
    "america/boise": "America/Denver",
    "us/mountain": "America/Denver",
    "america/los_angeles": "America/Los_Angeles",
    "us/pacific": "America/Los_Angeles",
    "america/phoenix": "America/Phoenix",
    "us/arizona": "America/Phoenix",
    "america/anchorage": "America/Anchorage",
    "us/alaska": "America/Anchorage",
    "pacific/honolulu": "Pacific/Honolulu",
    "us/hawaii": "Pacific/Honolulu"
};

const normalizeUsTimeZoneValue = (value: string): string => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
        return "";
    }

    const normalized = timeZoneAliases[trimmed.toLowerCase()] || trimmed;
    return usTimeZoneChoices.some(choice => choice.value === normalized) ? normalized : "";
};

const greetingDefault = "Thanks for calling Voicify Pizza. Is this for pickup or delivery?";
const quickServeGreetingDefault = "Thanks for calling {{constant.location_name}}! Will this be for pickup or delivery?";
const fullServiceGreetingDefault = "Thanks for calling {{constant.location_name}}! I'm here to answer your questions or take your order. How can I help?";
const healthcareGreetingDefault = "Hi there and thank you for choosing {ClientName}. My name is {AssistantName}. I'm your Smart AI Assistant. I can help with scheduling appointments and answering general questions. Just so you know, this call is being recorded for quality purposes. How may I assist you?";
const greetingReferenceGuidance = "Text highlighted in blue will automatically update based on your other form responses. Please do not edit or delete these fields.";
export const customVoiceOptionValue = "Custom";
const supportedOnboardingVoiceValues = voiceOptions.map(option => option.value);

export const getOnboardingVoiceOption = (value: string): SparkVoiceOption | undefined => getSparkVoiceOption(value);

const createOnboardingVoiceChoices = (defaultVoice: string): Array<{ label: string; value: string }> => {
    const orderedValues = [
        defaultVoice,
        ...supportedOnboardingVoiceValues.filter(value => value !== defaultVoice)
    ];

    return orderedValues.map(value => {
        const voiceOption = getOnboardingVoiceOption(value);
        return {
            label: voiceOption?.label || value,
            value
        };
    }).concat([{ label: customVoiceOptionValue, value: customVoiceOptionValue }]);
};

const voiceSelectionStep = (
    defaultVoice: string,
    openGreetingVariables: string[],
    closedGreetingVariables: string[]
): OnboardingStepConfig => {
    const choices = createOnboardingVoiceChoices(defaultVoice);

    return {
        id: "voice-selection",
        title: "Voice",
        questions: [
            {
                id: "voiceSelection",
                title: "Choose your assistant's voice",
                supportText: "Select the voice your assistant will use on calls.",
                section: "Voice",
                type: "choice",
                ownership: "organization",
                responseOnly: true,
                allowLocationOverride: true,
                saveEnabled: true,
                choices,
                defaultValue: defaultVoice,
                isValid: value => typeof value === "string" && choices.some(choice => choice.value === value),
                getSaveInstructions: (value, context) => {
                    if (typeof value !== "string" || value === customVoiceOptionValue) {
                        return [];
                    }

                    const storedAnswers = context.storedAnswers || {};
                    const buildInstructions = (variables: string[], answerId: string): OnboardingSaveInstruction[] => {
                        const answerValue = storedAnswers[answerId];
                        return variables.reduce((instructions, associatedVariable) => {
                            const sourceValue = typeof answerValue === "string" && answerValue.trim()
                                ? answerValue
                                : getModuleFieldValue(context.moduleFieldLookup, associatedVariable, "");
                            const updatedValue = updateVfyVoiceName(sourceValue, value);
                            if (updatedValue && updatedValue !== sourceValue) {
                                instructions.push({ associatedVariable, value: updatedValue });
                            }
                            return instructions;
                        }, [] as OnboardingSaveInstruction[]);
                    };

                    return [
                        ...buildInstructions(openGreetingVariables, "englishGreeting"),
                        ...buildInstructions(closedGreetingVariables, "closedGreeting")
                    ];
                }
            },
            notMappedQuestion({
                id: "customVoiceDetails",
                title: "Custom voice details",
                helperText: "We'll discuss your custom voice when we connect next.",
                placeholder: "Describe the tone, accent, age, or style you have in mind.",
                section: "Voice",
                type: "textarea",
                ownership: "both",
                responseOnly: true,
                defaultValue: "",
                isVisible: () => false,
                isValid: value => typeof value === "string" && value.trim().length > 0
            })
        ]
    };
};

const allHoursProvided = (value: WeeklyHoursAnswerValue): boolean => {
    return [
        value.mondayHours,
        value.tuesdayHours,
        value.wednesdayHours,
        value.thursdayHours,
        value.fridayHours,
        value.saturdayHours,
        value.sundayHours
    ].every(dayValue => dayValue.trim().length > 0);
};

const stringQuestion = (config: Omit<OnboardingQuestionConfig, "type"> & { type?: OnboardingQuestionInputType }): OnboardingQuestionConfig => {
    const saveEnabled = config.saveEnabled !== false;

    return {
        saveEnabled,
        getInitialValue: ({ moduleFieldLookup }) => {
            const variables = config.associatedVariables || (config.associatedVariable ? [config.associatedVariable] : []);
            return variables.length > 0 ? getFirstModuleFieldValue(moduleFieldLookup, variables, config.defaultValue || "") : (config.defaultValue || "");
        },
        isValid: (value) => config.optional ? true : typeof value === "string" && value.trim().length > 0,
        getSaveInstructions: (value) => {
            if (!saveEnabled || !config.associatedVariable) {
                return [];
            }

            return [{
                associatedVariable: config.associatedVariable,
                value: typeof value === "string" ? value.trim() : ""
            }];
        },
        type: config.type || "text",
        ...config
    };
};

const notMappedQuestion = (config: Omit<OnboardingQuestionConfig, "saveEnabled" | "getSaveInstructions">): OnboardingQuestionConfig => ({
    ...config,
    saveEnabled: false,
    getSaveInstructions: () => []
});

const targetLaunchDateQuestion = (section: string): OnboardingQuestionConfig => stringQuestion({
    id: "targetLaunchDate",
    title: "Target go-live date",
    supportText: "Enter your target go-live date. Don’t worry, this can always be adjusted as timelines shift, but it gives us something to work toward. We typically recommend targeting about three weeks after kickoff.",
    placeholder: "MM/DD/YYYY",
    section,
    ownership: "location",
    responseOnly: true,
    getInitialValue: ({ application }) => {
        if (application?.targetLaunchDate) {
            const date = new Date(application.targetLaunchDate);
            if (!isNaN(date.getTime())) {
                const month = String(date.getUTCMonth() + 1).padStart(2, "0");
                const day = String(date.getUTCDate()).padStart(2, "0");
                return `${month}/${day}/${date.getUTCFullYear()}`;
            }
        }
        return "";
    },
    normalizeInput: (value) => {
        const digits = value.replace(/\D/g, "").slice(0, 8);
        if (digits.length <= 2) {
            return digits;
        }
        if (digits.length <= 4) {
            return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    },
    isValid: (value) => {
        const dateValue = typeof value === "string" ? value.trim() : "";
        const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateValue);
        if (!match) {
            return false;
        }

        const month = parseInt(match[1], 10);
        const day = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);
        const date = new Date(year, month - 1, day);
        return year >= 1000
            && date.getFullYear() === year
            && date.getMonth() === month - 1
            && date.getDate() === day;
    }
});

const foodOrderingFlow: OnboardingFlowConfig = {
    type: "food-ordering",
    title: "Food ordering onboarding",
    steps: [
        {
            id: "general-info",
            title: "General Info",
            questions: [
                notMappedQuestion(stringQuestion({
                    id: "firstName",
                    title: "Your first name",
                    helperText: "Who should we list as the main onboarding contact?",
                    placeholder: "Taylor",
                    section: "General Info",
                    associatedVariable: "__TODO_CONTACT_FIRST_NAME__",
                    ownership: "both",
                    responseOnly: true
                })),
                notMappedQuestion(stringQuestion({
                    id: "lastName",
                    title: "Your last name",
                    helperText: "Who should we list as the main onboarding contact?",
                    placeholder: "Smith",
                    section: "General Info",
                    associatedVariable: "__TODO_CONTACT_LAST_NAME__",
                    ownership: "both",
                    responseOnly: true
                })),
                notMappedQuestion(stringQuestion({
                    id: "contactEmail",
                    title: "Your email",
                    helperText: "We will use this if we need to follow up.",
                    placeholder: "office@example.com",
                    section: "General Info",
                    type: "text",
                    associatedVariable: "__TODO_CONTACT_EMAIL__",
                    ownership: "both",
                    responseOnly: true
                })),
                stringQuestion({
                    id: "storeName",
                    title: "Store name",
                    supportText: "This name will be used in the assistant’s greeting. Enter it exactly as you’d like the assistant to say it. Make sure you include the restaurant name and location if applicable (e.g. Voicify Pizza on Chicago Street)",
                    placeholder: "Downtown Pizza",
                    section: "General Info",
                    associatedVariable: "{LocationName}",
                    associatedVariables: ["{LocationName}", "{Webhook-Restaurant-Name}"],
                    ownership: "location",
                    getInitialValue: ({ application, moduleFieldLookup }) => getModuleFieldValue(moduleFieldLookup, "{LocationName}", application.name || ""),
                    getSaveInstructions: value => {
                        const storeName = typeof value === "string" ? value.trim() : "";
                        return [
                            { associatedVariable: "{LocationName}", value: storeName },
                            { associatedVariable: "{Webhook-Restaurant-Name}", value: storeName }
                        ];
                    }
                }),
                stringQuestion({
                    id: "assistantName",
                    title: "Assistant name",
                    helperText: "This is the name your AI assistant will introduce itself with.",
                    placeholder: "Linda",
                    section: "General Info",
                    defaultValue: "Linda",
                    associatedVariable: "{Assistant-Name}",
                    getInitialValue: ({ moduleFieldLookup }) => getModuleFieldValue(moduleFieldLookup, "{Assistant-Name}", "Linda")
                }),
                targetLaunchDateQuestion("General Info"),
                stringQuestion({
                    id: "address",
                    title: "Address",
                    helperText: "Your assistant uses this when people ask for directions.",
                    placeholder: "123 Main Street, Nashville, TN 37203",
                    section: "General Info",
                    associatedVariable: "{Address}",
                    ownership: "location"
                }),
                notMappedQuestion({
                    id: "posHoursInfo",
                    title: "Regular hours",
                    helperText: "Your assistant uses your POS hours. Take a moment now to make sure they're up to date.",
                    continueLabel: "POS Hours Are Up to Date",
                    section: "General Info",
                    type: "info",
                    optional: true,
                    defaultValue: "",
                    ownership: "location",
                    responseOnly: true
                }),
                stringQuestion({
                    id: "timeZone",
                    title: "Time zone",
                    helperText: "This helps your assistant answer hours correctly.",
                    section: "General Info",
                    type: "select",
                    choices: usTimeZoneChoices,
                    associatedVariable: "{Webhook-Time-Zone}",
                    normalizeInput: normalizeUsTimeZoneValue,
                    getInitialValue: ({ moduleFieldLookup }) => normalizeUsTimeZoneValue(getFirstModuleFieldValue(moduleFieldLookup, ["{Webhook-Time-Zone}"], "")),
                    getSaveInstructions: (value) => {
                        const cleaned = normalizeUsTimeZoneValue(value as string);
                        return [
                            { associatedVariable: "{Webhook-Time-Zone}", value: cleaned }
                        ];
                    }
                }),
                {
                    id: "weeklyHours",
                    title: "Regular hours",
                    supportText: "These are your standard weekly hours. You can add specific closures like holidays or occasional Fridays, for example, on the next page.",
                    section: "General Info",
                    type: "hours",
                    associatedVariables: ["{MONDAY}", "{TUESDAY}", "{WEDNESDAY}", "{THURSDAY}", "{FRIDAY}", "{SATURDAY}", "{SUNDAY}"],
                    ownershipVariables: ["{MONDAY}", "{TUESDAY}", "{WEDNESDAY}", "{THURSDAY}", "{FRIDAY}", "{SATURDAY}", "{SUNDAY}"],
                    saveEnabled: true,
                    getInitialValue: ({ moduleFieldLookup }) => getWeeklyHoursAnswerFromLookup(moduleFieldLookup),
                    isValid: (value) => !!value && typeof value === "object" && allHoursProvided(value as WeeklyHoursAnswerValue),
                    getSaveInstructions: (value) => getWeeklyHoursUpdates(value as WeeklyHoursAnswerValue)
                },
                {
                    id: "specialHours",
                    title: "Holiday closures",
                    helperText: "Tap any holidays you close for. We'll add every 2026-2030 date automatically.",
                    section: "General Info",
                    type: "special-dates",
                    optional: true,
                    saveEnabled: true,
                    associatedVariable: "DISABLED_DAYS",
                    ownershipVariables: ["DISABLED_DAYS"],
                    getInitialValue: ({ moduleFieldLookup }) => getSpecialDatesAnswerFromLookup(moduleFieldLookup),
                    isValid: () => true,
                    getSaveInstructions: (value) => [{
                        associatedVariable: "DISABLED_DAYS",
                        value: parseSpecialDatesForSave(value as any)
                    }]
                }
            ]
        },
        {
            id: "phone-numbers",
            title: "Phone Numbers",
            questions: [
                notMappedQuestion({
                    ...stringQuestion({
                        id: "publicPhoneNumber",
                        title: "Public phone number",
                        supportText: "When we're ready to go live, customer calls here will be forwarded to your AI assistant.",
                        placeholder: "(555) 123-4567",
                        section: "Phone Numbers",
                        type: "tel",
                        associatedVariable: "__TODO_PUBLIC_PHONE__",
                        ownership: "location",
                        responseOnly: true,
                        normalizeInput: formatPhoneNumber,
                        isValid: (value) => getPhoneDigitsForSave(value as string).length >= 10
                    }),
                    helperText: "This is the main number customers call."
                }),
                stringQuestion({
                    id: "transferPhoneNumber",
                    title: "Transfer phone number",
                    helperText: "Calls transfer here when someone needs a person.",
                    supportText: "This is your direct store line for human handoffs. We'll transfer customers here when needed. If you don't have a second number, your provider can help you configure one.",
                    skipLabel: "No second number yet",
                    placeholder: "(555) 987-6543",
                    section: "Phone Numbers",
                    type: "tel",
                    optional: true,
                    associatedVariable: "{{session.returntostorenumber}}",
                    ownership: "location",
                    normalizeInput: formatPhoneNumber,
                    isValid: (value) => getPhoneDigitsForSave(value as string).length >= 10,
                    getInitialValue: ({ moduleFieldLookup }) => formatPhoneNumber(getModuleFieldValue(moduleFieldLookup, "{{session.returntostorenumber}}")),
                    getSaveInstructions: (value) => [{
                        associatedVariable: "{{session.returntostorenumber}}",
                        value: getPhoneDigitsForSave(value as string)
                    }]
                })
            ]
        },
        {
            id: "greetings",
            title: "Greetings",
            questions: [
                {
                    id: "greetingMode",
                    title: "Default greeting",
                    supportText: greetingReferenceGuidance,
                    emphasizeSupportText: true,
                    section: "Greetings",
                    type: "choice",
                    choices: [
                        { label: "Quick-serve / carryout & delivery", value: "quick-serve" },
                        { label: "Full-service / dine-in", value: "full-service" },
                        { label: "Use my own greeting", value: "use-own" }
                    ],
                    defaultValue: "quick-serve",
                    saveEnabled: true,
                    isValid: (value) => value === "quick-serve" || value === "full-service" || value === "use-own",
                    associatedVariables: [
                        "{Item-Welcome-Message-New-User-Response}",
                        "{Item-Welcome-Message-Repeat-User-Response-1}",
                        "{Item-Welcome-Message-Repeat-User-Response-2}",
                        "{Item-Welcome-Message-Repeat-User-Response-3}",
                        "{CLOSED_WELCOME_MESSAGE}",
                        "{Item-Welcome-Message-Closed-For-Day-Response}"
                    ],
                    ownershipVariables: [
                        "{Item-Welcome-Message-New-User-Response}",
                        "{Item-Welcome-Message-Repeat-User-Response-1}",
                        "{Item-Welcome-Message-Repeat-User-Response-2}",
                        "{Item-Welcome-Message-Repeat-User-Response-3}",
                        "{CLOSED_WELCOME_MESSAGE}",
                        "{Item-Welcome-Message-Closed-For-Day-Response}"
                    ],
                    getInitialValue: ({ moduleFieldLookup }) => {
                        const savedOpenValue = getModuleFieldValue(moduleFieldLookup, "{Item-Welcome-Message-New-User-Response}", "").trim();
                        const savedClosedValue = getModuleFieldValue(moduleFieldLookup, "{CLOSED_WELCOME_MESSAGE}", "").trim();

                        if (!savedOpenValue && !savedClosedValue) {
                            return "quick-serve";
                        }

                        const quickServeOpen = sanitizeMultilineText(quickServeGreetingDefault).trim();
                        const quickServeClosed = sanitizeMultilineText(buildClosedGreeting(quickServeGreetingDefault)).trim();

                        const fullServiceOpen = sanitizeMultilineText(fullServiceGreetingDefault).trim();
                        const fullServiceClosed = sanitizeMultilineText(buildClosedGreeting(fullServiceGreetingDefault)).trim();

                        if (savedOpenValue === quickServeOpen && savedClosedValue === quickServeClosed) {
                            return "quick-serve";
                        }

                        if (savedOpenValue === fullServiceOpen && savedClosedValue === fullServiceClosed) {
                            return "full-service";
                        }

                        return "use-own";
                    },
                    normalizeInput: undefined,
                    getSaveInstructions: (value) => {
                        if (value === "use-own") {
                            return [];
                        }

                        const template = value === "full-service" ? fullServiceGreetingDefault : quickServeGreetingDefault;
                        const cleaned = sanitizeMultilineText(template);
                        return [
                            { associatedVariable: "{Item-Welcome-Message-New-User-Response}", value: cleaned },
                            { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-1}", value: cleaned },
                            { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-2}", value: cleaned },
                            { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-3}", value: cleaned },
                            { associatedVariable: "{CLOSED_WELCOME_MESSAGE}", value: buildClosedGreeting(cleaned) },
                            { associatedVariable: "{Item-Welcome-Message-Closed-For-Day-Response}", value: buildClosedGreeting(cleaned) }
                        ];
                    }
                },
                {
                    ...stringQuestion({
                        id: "englishGreeting",
                        title: "Write your greeting when open",
                        helperText: "End your greeting with a question so callers know the assistant is ready to help.",
                        placeholder: greetingDefault,
                        section: "Greetings",
                        type: "textarea",
                        associatedVariable: "{Item-Welcome-Message-New-User-Response}",
                        associatedVariables: [
                            "{Item-Welcome-Message-New-User-Response}",
                            "{Item-Welcome-Message-Repeat-User-Response-1}",
                            "{Item-Welcome-Message-Repeat-User-Response-2}",
                            "{Item-Welcome-Message-Repeat-User-Response-3}"
                        ],
                        defaultValue: greetingDefault,
                        isVisible: () => false,
                        getSaveInstructions: (value) => {
                            const cleaned = sanitizeMultilineText(value as string);
                            return [
                                { associatedVariable: "{Item-Welcome-Message-New-User-Response}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-1}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-2}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-3}", value: cleaned }
                            ];
                        }
                    }),
                    isVisible: () => false
                },
                {
                    ...stringQuestion({
                        id: "closedGreeting",
                        title: "Write your greeting when closed",
                        helperText: "Be sure to include a statement explaining that you are closed.",
                        placeholder: buildClosedGreeting(greetingDefault),
                        section: "Greetings",
                        type: "textarea",
                        associatedVariable: "{CLOSED_WELCOME_MESSAGE}",
                        associatedVariables: [
                            "{CLOSED_WELCOME_MESSAGE}",
                            "{Item-Welcome-Message-Closed-For-Day-Response}"
                        ],
                        defaultValue: buildClosedGreeting(greetingDefault),
                        isVisible: () => false,
                        getSaveInstructions: (value) => {
                            const cleaned = sanitizeMultilineText(value as string);
                            return [
                                { associatedVariable: "{CLOSED_WELCOME_MESSAGE}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Closed-For-Day-Response}", value: cleaned }
                            ];
                        }
                    }),
                    isVisible: () => false
                }
            ]
        },
        voiceSelectionStep(
            defaultVoiceOption.value,
            [
                "{Item-Welcome-Message-New-User-Response}",
                "{Item-Welcome-Message-Repeat-User-Response-1}",
                "{Item-Welcome-Message-Repeat-User-Response-2}",
                "{Item-Welcome-Message-Repeat-User-Response-3}"
            ],
            ["{CLOSED_WELCOME_MESSAGE}", "{Item-Welcome-Message-Closed-For-Day-Response}"]
        ),
        {
            id: "delivery-payments",
            title: "Delivery & Payments",
            questions: [
                stringQuestion({
                    id: "allowDelivery",
                    title: "Should your assistant allow delivery orders?",
                    helperText: "Let callers place delivery orders by phone.",
                    section: "Delivery & Payments",
                    type: "choice",
                    associatedVariable: "{Webhook-Allow-Delivery}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Allow-Delivery}"), "no"),
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Allow-Delivery}", value: booleanAnswerToString(value as string) }]
                }),
                stringQuestion({
                    id: "deliveryMinimumEnabled",
                    title: "Require a delivery minimum",
                    helperText: "Say yes if delivery orders need a minimum amount.",
                    section: "Delivery & Payments",
                    type: "choice",
                    isVisible: (answers) => answers.allowDelivery === "yes",
                    associatedVariable: "{Webhook-Disable-Minimum-Charge-Prompt}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Disable-Minimum-Charge-Prompt}"), "no") === "yes" ? "no" : "yes",
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Disable-Minimum-Charge-Prompt}", value: inverseBooleanAnswerToString(value as string) }]
                }),
                stringQuestion({
                    id: "deliveryMinimumAmount",
                    title: "Delivery minimum amount",
                    helperText: "Enter the minimum order amount if you use one.",
                    placeholder: "15.00",
                    section: "Delivery & Payments",
                    type: "currency",
                    isVisible: (answers) => answers.allowDelivery === "yes" && answers.deliveryMinimumEnabled === "yes",
                    associatedVariable: "{Webhook-Delivery-Order-Minimum-Charge}",
                    optional: true
                }),
                stringQuestion({
                    id: "deliveryPaymentMethod",
                    title: "Delivery payment method",
                    supportText: "Select all of the ways customers can pay for delivery. Prepaid orders use a texted payment link.",
                    section: "Delivery & Payments",
                    type: "choice",
                    allowMultipleChoices: true,
                    isVisible: (answers) => answers.allowDelivery === "yes",
                    choices: paymentChoices,
                    defaultValue: serializeMultiChoiceValue(["prepaid"], paymentChoices.map(choice => choice.value)),
                    associatedVariables: [
                        "{Webhook-Allow-Delivery-With-Cash}",
                        "{Webhook-Allow-Delivery-With-Card-At-Door}",
                        "{Webhook-Allow-Delivery-With-Card}"
                    ],
                    ownershipVariables: [
                        "{Webhook-Allow-Delivery-With-Cash}",
                        "{Webhook-Allow-Delivery-With-Card-At-Door}",
                        "{Webhook-Allow-Delivery-With-Card}"
                    ],
                    getInitialValue: ({ moduleFieldLookup }) => serializeMultiChoiceValue([
                        getModuleFieldValue(moduleFieldLookup, "{Webhook-Allow-Delivery-With-Card}", "").trim().toLowerCase() === "true" ? "prepaid" : "",
                        getModuleFieldValue(moduleFieldLookup, "{Webhook-Allow-Delivery-With-Cash}", "").trim().toLowerCase() === "true" ? "cash" : "",
                        getModuleFieldValue(moduleFieldLookup, "{Webhook-Allow-Delivery-With-Card-At-Door}", "").trim().toLowerCase() === "true" ? "card_with_driver" : ""
                    ].filter(Boolean), paymentChoices.map(choice => choice.value)),
                    isValid: (value) => parseMultiChoiceValue(value as string, paymentChoices.map(choice => choice.value)).length > 0,
                    getSaveInstructions: (value) => {
                        const selected = new Set(parseMultiChoiceValue(value as string, paymentChoices.map(choice => choice.value)));
                        return [
                            { associatedVariable: "{Webhook-Allow-Delivery-With-Cash}", value: selected.has("cash") ? "true" : "false" },
                            { associatedVariable: "{Webhook-Allow-Delivery-With-Card-At-Door}", value: selected.has("card_with_driver") ? "true" : "false" },
                            { associatedVariable: "{Webhook-Allow-Delivery-With-Card}", value: selected.has("prepaid") ? "true" : "false" }
                        ];
                    }
                }),
                stringQuestion({
                    id: "pickupPrepaidThreshold",
                    title: "Pickup prepaid threshold",
                    helperText: "Require payment in advance for pickup orders over this amount.",
                    supportText: "Require payment in advance for pickup orders over this amount.",
                    placeholder: "100",
                    section: "Delivery & Payments",
                    type: "currency",
                    associatedVariable: "{Webhook-Max-Order-Total-Allowed}",
                    defaultValue: "100",
                    optional: true
                }),
                stringQuestion({
                    id: "allowFutureOrders",
                    title: "Allow future orders",
                    supportText: "Let customers schedule orders ahead of time. This includes during non-operating hours. Customers can place an order for the next available open time.",
                    section: "Delivery & Payments",
                    type: "choice",
                    associatedVariable: "{Webhook-Allow-Scheduling-For-Future-Date}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Allow-Scheduling-For-Future-Date}"), "no"),
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Allow-Scheduling-For-Future-Date}", value: booleanAnswerToString(value as string) }]
                })
            ]
        },
        {
            id: "sales-upsells",
            title: "Sales, Upsells, Add-Ons",
            questions: [
                stringQuestion({
                    id: "upsellsBeforeCheckout",
                    title: "Upsells",
                    supportText: "List three items you want upsold before checkout. Enter the items exactly as they should be offered to the customer, including a natural quantifier (for example: \"some cheesy bread,\" \"an order of cinnamon sticks,\" or \"a 2 liter soft drink\"). The assistant will automatically exclude anything already in the cart.",
                    placeholder: "e.g. some cheesy bread",
                    itemPlaceholders: ["e.g. some cheesy bread", "e.g. an order of cinnamon sticks", "e.g. a 2 liter soft drink"],
                    section: "Sales, Upsells, Add-Ons",
                    type: "item-list",
                    ownership: "organization",
                    allowLocationOverride: true,
                    associatedVariables: ["{UPSELL_ITEM_1}", "{UPSELL_ITEM_2}", "{UPSELL_ITEM_3}", "UPSELL_VAR"],
                    ownershipVariables: ["{UPSELL_ITEM_1}", "{UPSELL_ITEM_2}", "{UPSELL_ITEM_3}"],
                    getInitialValue: ({ moduleFieldLookup }) => {
                        const item1 = getModuleFieldValue(moduleFieldLookup, "{UPSELL_ITEM_1}", "");
                        const item2 = getModuleFieldValue(moduleFieldLookup, "{UPSELL_ITEM_2}", "");
                        const item3 = getModuleFieldValue(moduleFieldLookup, "{UPSELL_ITEM_3}", "");

                        return `${item1},${item2},${item3}`;
                    },
                    isValid: (value) => {
                        const str = value as string;
                        if (!str.trim() || str.match(/^,*$/)) return false;
                        const parts = str.split(",");
                        return parts.length === 3 && parts.every(p => p.trim().length > 0 && p.trim().length < 100);
                    },
                    getSaveInstructions: (value) => {
                        const str = (value as string).trim();
                        if (!str || str.match(/^,*$/)) {
                            return [
                                { associatedVariable: "{UPSELL_ITEM_1}", value: "" },
                                { associatedVariable: "{UPSELL_ITEM_2}", value: "" },
                                { associatedVariable: "{UPSELL_ITEM_3}", value: "" },
                                { associatedVariable: "UPSELL_VAR", value: "false" }
                            ];
                        }
                        const parts = str.split(",");
                        const items = parts.map((s: string) => s.trim()).filter(Boolean);
                        if (items.length < 3) {
                            return [
                                { associatedVariable: "{UPSELL_ITEM_1}", value: "" },
                                { associatedVariable: "{UPSELL_ITEM_2}", value: "" },
                                { associatedVariable: "{UPSELL_ITEM_3}", value: "" },
                                { associatedVariable: "UPSELL_VAR", value: "false" }
                            ];
                        }
                        return [
                            { associatedVariable: "{UPSELL_ITEM_1}", value: items[0] },
                            { associatedVariable: "{UPSELL_ITEM_2}", value: items[1] },
                            { associatedVariable: "{UPSELL_ITEM_3}", value: items[2] },
                            { associatedVariable: "UPSELL_VAR", value: "true" }
                        ];
                    }
                }),
                notMappedQuestion(stringQuestion({
                    id: "popularItems",
                    title: "Popular items to mention",
                    supportText: "Enter exactly 3 popular items you'd like the assistant to recommend.",
                    placeholder: "e.g. pepperoni pizza",
                    itemPlaceholders: ["e.g. pepperoni pizza", "e.g. loaded nachos", "e.g. chocolate chip cookie"],
                    section: "Sales, Upsells, Add-Ons",
                    type: "item-list",
                    ownership: "organization",
                    allowLocationOverride: true,
                    responseOnly: true,
                    defaultValue: ",,",
                    isValid: (value) => {
                        const str = value as string;
                        if (!str.trim() || str.match(/^,*$/)) return false;
                        const parts = str.split(",");
                        const isItemList = parts.length === 3 && parts.every(p => p.trim().length < 100);
                        if (isItemList) return parts.every(p => p.trim().length > 0);
                        return str.length > 20; // saved prompt
                    }
                })),
                stringQuestion({
                    id: "hasCoupons",
                    title: "Allow coupons in POS to be applied by assistant",
                    supportText: "Choose if your assistant should auto-apply coupons/deals without a code. This is different from specials/SPOs/LTOs that are set in your POS.",
                    section: "Sales, Upsells, Add-Ons",
                    type: "choice",
                    associatedVariable: "{Webhook-Allow-Promotions}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Allow-Promotions}"), "no"),
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Allow-Promotions}", value: booleanAnswerToString(value as string) }]
                }),
                notMappedQuestion(stringQuestion({
                    id: "hasCouponsAdditionalInfo",
                    title: "Additional coupon info",
                    placeholder: "Any additional details about your coupons or deals...",
                    section: "Sales, Upsells, Add-Ons",
                    type: "textarea",
                    associatedVariable: "__TODO_HAS_COUPONS_ADDITIONAL_INFO__",
                    ownershipVariables: ["{Webhook-Allow-Promotions}"],
                    optional: true,
                    responseOnly: true,
                    isVisible: () => false
                })),
                stringQuestion({
                    id: "autoApplyDeals",
                    title: "Should the assistant automatically apply deals the caller's order qualifies for?",
                    supportText: "Choose yes if qualifying deals should be applied automatically without the caller asking for them.",
                    section: "Sales, Upsells, Add-Ons",
                    type: "choice",
                    optional: true,
                    isVisible: (answers) => answers.hasCoupons === "yes",
                    associatedVariable: "{Webhook-Disable-Automatic-Coupons}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Disable-Automatic-Coupons}"), "no") === "yes" ? "no" : "yes",
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Disable-Automatic-Coupons}", value: inverseBooleanAnswerToString(value as string) }]
                })
            ]
        },
        {
            id: "telephony-requirements",
            title: "Telephony Requirements",
            questions: [
                notMappedQuestion({
                    id: "telephonyRequirementsInfo",
                    title: "Telephony Requirements",
                    section: "Telephony Requirements",
                    type: "info",
                    optional: true,
                    continueLabel: "I Contacted My Phone Provider",
                    ownership: "location",
                    responseOnly: true
                })
            ]
        },
        {
            id: "more-info",
            title: "More Info",
            questions: [
                stringQuestion({
                    id: "locationFaq",
                    title: "Common questions the assistant should know the answer to",
                    supportText: "Add common questions and answers the assistant should know for this location. Your address, regular hours, and closures are included automatically; add other helpful details here. For example: \"Parking is behind the building. This location does not have a drive-thru. We do have a gluten-free pizza crust. No, we do not take reservations.\"",
                    placeholder: "Parking is behind the building. This location does not have a drive-thru. We do have a gluten-free pizza crust. No, we do not take reservations.",
                    section: "More Info",
                    type: "textarea",
                    associatedVariable: "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}",
                    optional: true,
                    ownership: "location",
                    getInitialValue: ({ moduleFieldLookup, storedAnswers }) => {
                        const saved = getModuleFieldValue(moduleFieldLookup, "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}", "");
                        const savedOrStored = saved || (storedAnswers?.["locationFaq"] as string || "");
                        return extractUserCustomText(savedOrStored);
                    },
                    getSaveInstructions: (value, { storedAnswers, moduleFieldLookup }) => {
                        const userText = (value as string || "").trim();
                        const fullText = formatLocationFaqParagraph(userText, storedAnswers, moduleFieldLookup);
                        return [{ associatedVariable: "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}", value: fullText }];
                    }
                }),
                notMappedQuestion(stringQuestion({
                    id: "specialNotes",
                    title: "Anything else we should know",
                    supportText: "Add anything helpful for the team here.",
                    placeholder: "We close early when severe weather is expected.",
                    section: "More Info",
                    type: "textarea",
                    associatedVariable: "__TODO_SPECIAL_NOTES__",
                    optional: true,
                    ownership: "both",
                    responseOnly: true
                }))
            ]
        }
    ]
};

const healthcareFlow: OnboardingFlowConfig = {
    type: "healthcare",
    title: "Healthcare onboarding",
    steps: [
        {
            id: "basic-info",
            title: "Basic Info",
            questions: [
                notMappedQuestion(stringQuestion({
                    id: "firstName",
                    title: "Your first name",
                    helperText: "Who should we list as the main onboarding contact?",
                    placeholder: "Taylor",
                    section: "Basic Info",
                    associatedVariable: "__TODO_CONTACT_FIRST_NAME__",
                    ownership: "both",
                    responseOnly: true
                })),
                notMappedQuestion(stringQuestion({
                    id: "lastName",
                    title: "Your last name",
                    helperText: "Who should we list as the main onboarding contact?",
                    placeholder: "Smith",
                    section: "Basic Info",
                    associatedVariable: "__TODO_CONTACT_LAST_NAME__",
                    ownership: "both",
                    responseOnly: true
                })),
                notMappedQuestion(stringQuestion({
                    id: "contactEmail",
                    title: "Your email",
                    helperText: "We will use this if we need to follow up.",
                    placeholder: "office@example.com",
                    section: "Basic Info",
                    type: "text",
                    associatedVariable: "__TODO_CONTACT_EMAIL__",
                    ownership: "both",
                    responseOnly: true
                })),
                stringQuestion({
                    id: "officeName",
                    title: "Office name",
                    helperText: "Use the name patients know.",
                    placeholder: "Smile Dental Care",
                    section: "Basic Info",
                    associatedVariable: "{ClientName}",
                    ownership: "location",
                    getInitialValue: ({ application, moduleFieldLookup }) => getModuleFieldValue(moduleFieldLookup, "{ClientName}", application.name || "")
                }),
                stringQuestion({
                    id: "callHandling",
                    title: "Call handling",
                    skipLabel: "Skip for now",
                    section: "Basic Info",
                    optional: true,
                    type: "choice",
                    choices: callHandlingChoices,
                    allowMultipleChoices: true,
                    multiChoiceDelimiter: "|",
                    choicePresentation: "checkboxes",
                    useModuleFieldOptions: true,
                    saveEnabled: true,
                    associatedVariable: "{CallHandling}"
                }),
                stringQuestion({
                    id: "telephonyProvider",
                    title: "Telephony provider",
                    placeholder: "Your phone service provider",
                    skipLabel: "Skip for now",
                    section: "Basic Info",
                    optional: true,
                    saveEnabled: true,
                    associatedVariable: "{TelephonyProvider}"
                }),
                stringQuestion({
                    id: "callForwarding",
                    title: "Call forwarding",
                    skipLabel: "Skip for now",
                    section: "Basic Info",
                    optional: true,
                    type: "choice",
                    allowMultipleChoices: true,
                    multiChoiceDelimiter: "|",
                    choicePresentation: "checkboxes",
                    useModuleFieldOptions: true,
                    saveEnabled: true,
                    associatedVariable: "{CallForwarding}"
                }),
                stringQuestion({
                    id: "assistantName",
                    title: "Assistant name",
                    helperText: "This is the name your AI assistant will introduce itself with.",
                    placeholder: "Linda",
                    section: "Basic Info",
                    defaultValue: "Linda",
                    associatedVariable: "{AssistantName}",
                    getInitialValue: ({ moduleFieldLookup }) => getModuleFieldValue(moduleFieldLookup, "{AssistantName}", "Linda")
                }),
                targetLaunchDateQuestion("Basic Info")
            ]
        },
        {
            id: "contact-info",
            title: "Contact Info",
            questions: [
                stringQuestion({
                    id: "transferPhoneNumber",
                    title: "Transfer phone number",
                    helperText: "This is the main office number for transfers.",
                    supportText: "This is your direct store line for human handoffs. We'll transfer customers here when needed. If you don't have a second number, your provider can help you configure one.",
                    skipLabel: "No second number yet",
                    placeholder: "(555) 123-4567",
                    section: "Contact Info",
                    type: "tel",
                    optional: true,
                    associatedVariable: "{TransferTarget}",
                    ownership: "location",
                    normalizeInput: formatPhoneNumber,
                    isValid: (value) => getPhoneDigitsForSave(value as string).length >= 10,
                    getInitialValue: ({ moduleFieldLookup }) => formatPhoneNumber(getModuleFieldValue(moduleFieldLookup, "{TransferTarget}")),
                    getSaveInstructions: (value) => [{ associatedVariable: "{TransferTarget}", value: getPhoneDigitsForSave(value as string) }]
                }),
                stringQuestion({
                    id: "emergencyTransferPhoneNumber",
                    title: "Emergency transfer phone number",
                    helperText: "Use a backup number for urgent calls.",
                    placeholder: "(555) 555-1212",
                    section: "Contact Info",
                    type: "tel",
                    associatedVariable: "{EmergencyTransferTarget}",
                    ownership: "location",
                    normalizeInput: formatPhoneNumber,
                    isValid: (value) => getPhoneDigitsForSave(value as string).length >= 10,
                    getInitialValue: ({ moduleFieldLookup }) => formatPhoneNumber(getModuleFieldValue(moduleFieldLookup, "{EmergencyTransferTarget}")),
                    getSaveInstructions: (value) => [{ associatedVariable: "{EmergencyTransferTarget}", value: getPhoneDigitsForSave(value as string) }]
                }),
                stringQuestion({
                    id: "appointmentEmails",
                    title: "Appointment and message email recipients",
                    supportText: "Enter the office email addresses where you'd like appointment confirmations, left messages, and dropped call summaries sent. This is usually your main office email. Add yourself if you'd like to see emails coming through from Voicify.",
                    placeholder: "frontdesk@example.com",
                    section: "Contact Info",
                    type: "email-list",
                    associatedVariable: "{{OfficeAppointmentToEmail}}",
                    ownership: "location",
                    getInitialValue: ({ moduleFieldLookup }) => {
                        const raw = getModuleFieldValue(moduleFieldLookup, "{{OfficeAppointmentToEmail}}", "");
                        return raw.trim() || "";
                    },
                    getSaveInstructions: (value) => {
                        const emails = (value as string)
                            .split(",")
                            .map(e => e.trim())
                            .filter(Boolean)
                            .join(", ");
                        return [{ associatedVariable: "{{OfficeAppointmentToEmail}}", value: emails }];
                    }
                })
            ]
        },
        {
            id: "greetings",
            title: "Greetings",
            questions: [
                {
                    id: "greetingMode",
                    title: "Default greeting",
                    supportText: greetingReferenceGuidance,
                    emphasizeSupportText: true,
                    section: "Greetings",
                    type: "choice",
                    choices: [
                        { label: "Use the default greeting", value: "use-default" },
                        { label: "Use my own greeting", value: "use-own" }
                    ],
                    defaultValue: "use-default",
                    saveEnabled: true,
                    isValid: (value) => value === "use-default" || value === "use-own",
                    associatedVariables: [
                        "{WelcomeMessage}",
                        "{AfterHoursWelcomeMessage}"
                    ],
                    ownershipVariables: [
                        "{WelcomeMessage}",
                        "{AfterHoursWelcomeMessage}"
                    ],
                    getInitialValue: ({ moduleFieldLookup }) => {
                        const savedOpenValue = getModuleFieldValue(moduleFieldLookup, "{WelcomeMessage}", "").trim();
                        const savedClosedValue = getModuleFieldValue(moduleFieldLookup, "{AfterHoursWelcomeMessage}", "").trim();

                        if (!savedOpenValue && !savedClosedValue) {
                            return "use-default";
                        }

                        const healthcareDefaultOpen = sanitizeMultilineText(healthcareGreetingDefault).trim();
                        const healthcareDefaultClosed = sanitizeMultilineText(buildClosedGreeting(healthcareGreetingDefault)).trim();

                        if (savedOpenValue === healthcareDefaultOpen && savedClosedValue === healthcareDefaultClosed) {
                            return "use-default";
                        }

                        return "use-own";
                    },
                    normalizeInput: undefined,
                    getSaveInstructions: (value) => {
                        if (value !== "use-default") {
                            return [];
                        }

                        const cleaned = sanitizeMultilineText(healthcareGreetingDefault);
                        return [
                            { associatedVariable: "{WelcomeMessage}", value: cleaned },
                            { associatedVariable: "{AfterHoursWelcomeMessage}", value: buildClosedGreeting(cleaned) }
                        ];
                    }
                },
                {
                    ...stringQuestion({
                        id: "englishGreeting",
                        title: "Write your greeting when open",
                        helperText: "End your greeting with a form of \"How may I assist you?\" so callers know the assistant is ready to help.",
                        placeholder: healthcareGreetingDefault,
                        section: "Greetings",
                        type: "textarea",
                        associatedVariable: "{WelcomeMessage}",
                        associatedVariables: [
                            "{WelcomeMessage}"
                        ],
                        defaultValue: healthcareGreetingDefault,
                        isVisible: () => false,
                        getSaveInstructions: (value) => {
                            const cleaned = sanitizeMultilineText(value as string);
                            return [
                                { associatedVariable: "{WelcomeMessage}", value: cleaned }
                            ];
                        }
                    }),
                    isVisible: () => false
                },
                {
                    ...stringQuestion({
                        id: "closedGreeting",
                        title: "Write your greeting when closed",
                        helperText: "Be sure to include a statement explaining that you are closed.",
                        placeholder: buildClosedGreeting(healthcareGreetingDefault),
                        section: "Greetings",
                        type: "textarea",
                        associatedVariable: "{AfterHoursWelcomeMessage}",
                        associatedVariables: [
                            "{AfterHoursWelcomeMessage}"
                        ],
                        defaultValue: buildClosedGreeting(healthcareGreetingDefault),
                        isVisible: () => false,
                        getSaveInstructions: (value) => {
                            const cleaned = sanitizeMultilineText(value as string);
                            return [
                                { associatedVariable: "{AfterHoursWelcomeMessage}", value: cleaned }
                            ];
                        }
                    }),
                    isVisible: () => false
                }
            ]
        },
        voiceSelectionStep(defaultVoiceOption.value, ["{WelcomeMessage}"], ["{AfterHoursWelcomeMessage}"]),
        {
            id: "insurance",
            title: "Insurance",
            questions: [
                {
                    id: "insuranceCoverage",
                    title: "Insurance accepted, not accepted, and out of network",
                    supportText: "List the insurance providers you accept (in-network and out-of-network) and those you do not accept.",
                    section: "Insurance",
                    type: "insurance",
                    associatedVariable: "{InsuranceCoveragePrompt}",
                    ownershipVariables: ["{InsuranceCoveragePrompt}"],
                    saveEnabled: true,
                    getInitialValue: ({ moduleFieldLookup }) => {
                        const promptVal = getModuleFieldValue(moduleFieldLookup, "{InsuranceCoveragePrompt}", "");
                        return parseInsuranceCoverageFromPrompt(promptVal);
                    },
                    isValid: (value) => {
                        if (!value || typeof value !== "object") return false;
                        const v = value as InsuranceCoverageAnswerValue;
                        return v.accepted?.trim()?.length > 0 && v.outOfNetwork?.trim()?.length > 0 && v.notAccepted?.trim()?.length > 0;
                    },
                    getSaveInstructions: (value) => [{
                        associatedVariable: "{InsuranceCoveragePrompt}",
                        value: serializeInsuranceCoverageToPrompt(value as InsuranceCoverageAnswerValue)
                    }]
                }
            ]
        },
        {
            id: "policies",
            title: "Policies",
            questions: [
                {
                    id: "insuranceRulesMode",
                    title: "Insurance rules",
                    supportText: "Specify any rules or exceptions regarding insurance.",
                    section: "Policies",
                    type: "choice",
                    choices: [
                        { label: "No rules", value: "no-rules" },
                        { label: "Yes, we have special rules", value: "has-rules" }
                    ],
                    defaultValue: "no-rules",
                    saveEnabled: true,
                    ownershipVariables: ["{Custom-Insurance-Rules}"],
                    isValid: (value) => value === "no-rules" || value === "has-rules",
                    getInitialValue: () => "no-rules",
                    normalizeInput: undefined,
                    getSaveInstructions: () => []
                },
                {
                    ...stringQuestion({
                        id: "insuranceRules",
                        title: "Insurance rules",
                        placeholder: "If the patient is unsure, we transfer them to the front desk.",
                        section: "Policies",
                        type: "textarea",
                        associatedVariable: "{Custom-Insurance-Rules}",
                        ownershipVariables: ["{Custom-Insurance-Rules}"],
                        isVisible: () => false
                    }),
                    isVisible: () => false
                },
                {
                    id: "cancellationFeeMode",
                    title: "Cancellation fee",
                    section: "Policies",
                    type: "choice",
                    choices: [
                        { label: "No fee", value: "no-fee" },
                        { label: "Yes, we charge a fee", value: "has-fee" }
                    ],
                    defaultValue: "no-fee",
                    saveEnabled: true,
                    ownershipVariables: ["{CancelationFee}"],
                    isValid: (value) => value === "no-fee" || value === "has-fee",
                    getInitialValue: () => "no-fee",
                    normalizeInput: undefined,
                    getSaveInstructions: () => []
                },
                {
                    ...stringQuestion({
                        id: "cancellationFee",
                        title: "Cancellation fee",
                        placeholder: "50",
                        section: "Policies",
                        type: "currency",
                        associatedVariable: "{CancelationFee}",
                        ownershipVariables: ["{CancelationFee}"],
                        isVisible: () => false
                    }),
                    isVisible: () => false
                },
                {
                    id: "cancellationWindowMode",
                    title: "Cancellation window",
                    section: "Policies",
                    type: "choice",
                    choices: [
                        { label: "No window", value: "no-window" },
                        { label: "Yes, we have a cancellation window", value: "has-window" }
                    ],
                    defaultValue: "no-window",
                    saveEnabled: true,
                    ownershipVariables: ["{Webhook-Non-Cancellable-Window-Days}"],
                    isValid: (value) => value === "no-window" || value === "has-window",
                    getInitialValue: (context) => {
                        const lookupValue = getFirstModuleFieldValue(
                            context.moduleFieldLookup,
                            ["{Webhook-Non-Cancellable-Window-Days}"],
                            ""
                        ).trim();
                        return /^\d+$/.test(lookupValue) ? "has-window" : "no-window";
                    },
                    normalizeInput: undefined,
                    getSaveInstructions: (value, context) => {
                        const answerValue = context.storedAnswers?.["cancellationWindowDays"];
                        const savedValue = getFirstModuleFieldValue(
                            context.moduleFieldLookup,
                            ["{Webhook-Non-Cancellable-Window-Days}"],
                            ""
                        ).trim();
                        const days = typeof answerValue === "string" && /^\d+$/.test(answerValue.trim())
                            ? answerValue.trim()
                            : /^\d+$/.test(savedValue)
                                ? savedValue
                                : "1";
                        return [{
                            associatedVariable: "{Webhook-Non-Cancellable-Window-Days}",
                            value: value === "no-window" ? "-1" : days
                        }];
                    }
                },
                {
                    ...stringQuestion({
                        id: "cancellationWindowDays",
                        title: "Cancellation window in days",
                        placeholder: "1",
                        helperText: "We track cancellation windows by days, not hours.",
                        section: "Policies",
                        type: "number",
                        associatedVariable: "{Webhook-Non-Cancellable-Window-Days}",
                        ownershipVariables: ["{Webhook-Non-Cancellable-Window-Days}"],
                        isVisible: () => false,
                        isValid: (value) => typeof value === "string" && /^\d+$/.test(value.trim())
                    }),
                    normalizeInput: (value: string) => /^\d+$/.test(value.trim()) ? value.trim() : "",
                    getInitialValue: (context) => {
                        const lookupValue = getFirstModuleFieldValue(context.moduleFieldLookup, ["{Webhook-Non-Cancellable-Window-Days}"], "");
                        if (lookupValue && typeof lookupValue === "string" && /^\d+$/.test(lookupValue.trim())) {
                            return lookupValue.trim();
                        }
                        return "1";
                    },
                    isVisible: () => false
                },
                stringQuestion({
                    id: "cancellationPolicy",
                    title: "Cancellation policy",
                    supportText: "Include the exact wording you'd like the assistant to use when responding to questions about your cancellation policy.",
                    placeholder: "Please give at least 24 hours notice.",
                    section: "Policies",
                    type: "textarea",
                    associatedVariable: "{{CancellationPolicy}}",
                    ownershipVariables: ["{{CancellationPolicy}}"]
                }),
            ]
        },
        {
            id: "payment-appointments",
            title: "Payment & Appointments",
            questions: [
                stringQuestion({
                    id: "walkInsAccepted",
                    title: "Accept walk-ins",
                    helperText: "Say yes if patients can come in without an appointment.",
                    section: "Payment & Appointments",
                    type: "choice",
                    associatedVariable: "{WalkInsMessage}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{WalkInsMessage}"), "no"),
                    getSaveInstructions: (value) => [{ associatedVariable: "{WalkInsMessage}", value: booleanAnswerToString(value as string) }]
                }),
                {
                    id: "defaultProviderMode",
                    title: "Default Provider",
                    supportText: "The default provider for booking new patient appointments. This is required for new patient bookings. This is required for cloud9.",
                    section: "Payment & Appointments",
                    type: "choice",
                    choices: [
                        { label: "I have the provider ID", value: "has-id" },
                        { label: "I don't have the provider ID", value: "no-id" }
                    ],
                    defaultValue: "has-id",
                    saveEnabled: true,
                    ownership: "location",
                    getInitialValue: (context) => {
                        const idValue = getFirstModuleFieldValue(context.moduleFieldLookup, ["{DefaultProviderId}", "{defaultProviderId}"], "");
                        if (idValue && typeof idValue === "string" && idValue.trim().length > 0) {
                            return "has-id";
                        }
                        if (context.storedAnswers) {
                            if (context.storedAnswers["defaultProviderMode"]) {
                                return context.storedAnswers["defaultProviderMode"] as string;
                            }
                            if (context.storedAnswers["defaultProviderName"]) {
                                return "no-id";
                            }
                        }
                        return "has-id";
                    },
                    isValid: (value) => value === "has-id" || value === "no-id",
                    getSaveInstructions: () => []
                },
                {
                    ...stringQuestion({
                        id: "defaultProviderId",
                        title: "Default Provider ID",
                        placeholder: "e.g. 104",
                        section: "Payment & Appointments",
                        type: "text",
                        associatedVariable: "{DefaultProviderId}",
                        ownership: "location",
                        isVisible: () => false,
                        isValid: (value) => typeof value === "string" && value.trim().length > 0
                    }),
                    isVisible: () => false
                },
                {
                    ...notMappedQuestion(stringQuestion({
                        id: "defaultProviderName",
                        title: "Provider Full Name",
                        placeholder: "e.g. Dr. Jane Smith",
                        helperText: "we can look it up in the system for you.",
                        section: "Payment & Appointments",
                        type: "text",
                        ownership: "location",
                        isVisible: () => false,
                        isValid: (value) => typeof value === "string" && value.trim().length > 0
                    })),
                    isVisible: () => false
                },
                {
                    id: "billingPricingMode",
                    title: "Billing and pricing questions",
                    supportText: "How should billing or pricing questions be handled?",
                    section: "Payment & Appointments",
                    type: "choice",
                    choices: [
                        { label: "Yes, transfer to office", value: "transfer" },
                        { label: "No, don't transfer", value: "no-transfer" }
                    ],
                    defaultValue: "transfer",
                    saveEnabled: true,
                    ownershipVariables: ["{BillingInfo}", "{PricingInformation}"],
                    isValid: (value) => value === "transfer" || value === "no-transfer",
                    getInitialValue: () => "transfer",
                    normalizeInput: undefined,
                    getSaveInstructions: () => []
                },
                {
                    ...stringQuestion({
                        id: "billingPricingInfo",
                        title: "Billing and pricing questions",
                        helperText: "Include the exact wording you'd like the assistant to use when responding to questions about your billing and pricing.",
                        placeholder: "For billing questions, please visit our website or speak with our front desk during office hours.",
                        section: "Payment & Appointments",
                        type: "textarea",
                        associatedVariable: "{BillingInfo}",
                        associatedVariables: ["{BillingInfo}", "{PricingInformation}"],
                        ownershipVariables: ["{BillingInfo}", "{PricingInformation}"],
                        isVisible: () => false,
                        getSaveInstructions: (value) => {
                            const cleaned = sanitizeMultilineText(value as string);
                            return [
                                { associatedVariable: "{BillingInfo}", value: cleaned },
                                { associatedVariable: "{PricingInformation}", value: cleaned }
                            ];
                        }
                    }),
                    isVisible: () => false
                },
                stringQuestion({
                    id: "paymentPlanInfo",
                    title: "Payment plan information",
                    supportText: "Describe any payment plans or options you offer.",
                    placeholder: "We offer CareCredit and split-pay options.",
                    section: "Payment & Appointments",
                    type: "textarea",
                    associatedVariable: "{PaymentPlansPrompt}"
                }),
                notMappedQuestion({
                    id: "procedureTypes",
                    title: "Procedure types",
                    supportText: "Add each type of appointment you would like the assistant to be able to book, such as a new patient cleaning, emergency exam, or child cleaning.",
                    instructionLeadIn: "For each procedure, provide:",
                    fieldInstructions: [
                        {
                            label: "Description",
                            text: "Describe the appointment type and when the assistant should book it. Include any important criteria, such as whether it applies to new patients, existing patients, children, etc."
                        },
                        {
                            label: "Duration",
                            text: "Enter the typical appointment length in minutes."
                        },
                        {
                            label: "Procedure ID",
                            text: "If you know the corresponding Denticon Procedure Type ID, enter it here. If not, enter 0 as a placeholder."
                        }
                    ],
                    helperText: "We’ll review your entries and map them to the correct procedure types in your PMS.",
                    section: "Payment & Appointments",
                    type: "procedure-types",
                    associatedVariable: "__TODO_PROCEDURE_TYPES__",
                    locationPromotionFieldTitle: "Procedure Types",
                    optional: true,
                    ownership: "organization",
                    responseOnly: true,
                    allowLocationOverride: true,
                    getInitialValue: () => [],
                    isValid: (value) => Array.isArray(value) && value.every(entry => {
                        const description = typeof entry?.procedureTypeDescription === "string" ? entry.procedureTypeDescription.trim() : "";
                        const duration = entry?.appointmentDuration;
                        return description.length > 0 && duration !== undefined && duration !== null && !Number.isNaN(Number(duration));
                    })
                })
            ]
        },
        {
            id: "telephony-requirements",
            title: "Telephony Requirements",
            questions: [
                notMappedQuestion({
                    id: "telephonyRequirementsInfo",
                    title: "Telephony Requirements",
                    section: "Telephony Requirements",
                    type: "info",
                    optional: true,
                    continueLabel: "I Contacted My Phone Provider",
                    ownership: "location",
                    responseOnly: true
                })
            ]
        },
        {
            id: "hours-other",
            title: "Hours & Other",
            questions: [
                {
                    id: "weeklyHours",
                    title: "Regular hours",
                    supportText: "These are your standard weekly hours. You can add specific closures like holidays or occasional Fridays, for example, on the next page.",
                    section: "Basic Info",
                    type: "hours",
                    associatedVariables: ["{MONDAY}", "{TUESDAY}", "{WEDNESDAY}", "{THURSDAY}", "{FRIDAY}", "{SATURDAY}", "{SUNDAY}"],
                    ownershipVariables: ["{MONDAY}", "{TUESDAY}", "{WEDNESDAY}", "{THURSDAY}", "{FRIDAY}", "{SATURDAY}", "{SUNDAY}"],
                    saveEnabled: true,
                    getInitialValue: ({ moduleFieldLookup }) => getWeeklyHoursAnswerFromLookup(moduleFieldLookup),
                    isValid: (value) => !!value && typeof value === "object" && allHoursProvided(value as WeeklyHoursAnswerValue),
                    getSaveInstructions: (value) => getWeeklyHoursUpdates(value as WeeklyHoursAnswerValue)
                },
                {
                    id: "specialHours",
                    title: "Holiday & other closures",
                    helperText: "Select the holidays you close for — we'll automatically include all dates from 2026–2030. Need to add a one-off closure, like every other Friday you're closed? Set your regular Friday hours on the previous page, then add those specific dates below.",
                    section: "Hours & Other",
                    type: "special-dates",
                    optional: true,
                    saveEnabled: true,
                    associatedVariable: "DISABLED_DAYS",
                    ownershipVariables: ["DISABLED_DAYS"],
                    getInitialValue: ({ moduleFieldLookup }) => getSpecialDatesAnswerFromLookup(moduleFieldLookup),
                    isValid: () => true,
                    getSaveInstructions: (value) => [{
                        associatedVariable: "DISABLED_DAYS",
                        value: parseSpecialDatesForSave(value as any)
                    }]
                },
                notMappedQuestion(stringQuestion({
                    id: "additionalNotes",
                    title: "Anything else you would like to share",
                    helperText: "",
                    supportText: "Your AI Dental Assistant runs on a proven, standardized workflow. If you have any special instructions or preferences, feel free to share them here. We’ll do our best to accommodate them when we can!",
                    placeholder: "Please let patients know we validate parking after 3 PM.",
                    section: "Hours & Other",
                    type: "textarea",
                    associatedVariable: "__TODO_ADDITIONAL_NOTES__",
                    optional: true,
                    ownership: "both",
                    responseOnly: true
                }))
            ]
        }
    ]
};

export const onboardingFlows: Record<OnboardingFlowType, OnboardingFlowConfig> = {
    "food-ordering": foodOrderingFlow,
    healthcare: healthcareFlow
};

export const getOnboardingFlow = (type: OnboardingFlowType): OnboardingFlowConfig => onboardingFlows[type];
